import { Cached } from '@mui/icons-material';
import { Alert, Box, Typography } from '@mui/material';
import { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import React, { useMemo } from 'react';
import { ProgressButton } from 'src/common';
import ItemOrdering from 'src/common/components/ItemOrdering';
import { LoadingProgress } from 'src/common/store/types';
import {
  ClientReportsParameters,
  InitiateReportRequest,
  MdaReportTypes,
  ReportGeneration,
  ReportingParameter,
  ReportToSort,
  SavedParameter,
} from '../store/types';
import ReportingParameterSelect from './ReportingParameterSelect';
import { createInitiateReportRequest, getReportsToSort } from '../store/helper';

export interface ReportToolbarProps {
  savedParametersList: SavedParameter[];
  reportGeneration: ReportGeneration;
  generateProgress: LoadingProgress;
  getReportParametersByIdProgress: LoadingProgress;
  parameters: ClientReportsParameters;
  setSelectedReportingParameter: ActionCreatorWithPayload<SavedParameter | null, string>;
  setReportingParameter: ActionCreatorWithPayload<ReportingParameter, string>;
  setShowItemOderingModalParameter: ActionCreatorWithPayload<boolean, string>;
  setSelectedReportOrderParameter: ActionCreatorWithPayload<ReportToSort[], string>;
  handleClickMergeReports: () => void;
  setReportGeneration: ActionCreatorWithPayload<ReportGeneration, string>;
  initiateReportGeneration: (request: InitiateReportRequest) => void;
  isDataFromTriumph: boolean;
  clientId?: string;
  hasClientDirectAccess: boolean;
}

export const ReportingToolbar = ({
  reportGeneration,
  generateProgress,
  savedParametersList,
  parameters,
  getReportParametersByIdProgress,
  setSelectedReportingParameter,
  setReportingParameter,
  setReportGeneration,
  setShowItemOderingModalParameter,
  setSelectedReportOrderParameter,
  isDataFromTriumph,
  handleClickMergeReports,
  initiateReportGeneration,
  clientId,
  hasClientDirectAccess,
}: ReportToolbarProps): JSX.Element => {
  const { reportingParameter } = parameters;

  const reportPayload: { reportName: string; reportRequest: InitiateReportRequest } = useMemo(() => {
    return createInitiateReportRequest({
      isDataFromTriumph: isDataFromTriumph,
      clientId: Number(clientId),
      parameters: parameters,
      handleClickMergeReports: handleClickMergeReports,
    });
  }, [parameters]);

  const generateReportParameters = () => {
    const payload = reportPayload.reportRequest;

    setReportGeneration({
      ...reportGeneration,
      isDataFromTriumph: payload.isDataFromTriumph,
      isMergeReport: payload.mergeReportOrder.findIndex((x) => x == MdaReportTypes.MergeReport.id) > -1,
      clientId: payload.clientId,
      clientName: parameters.clientName,
      mergeReportOrder: payload.mergeReportOrder,
      dateRange: payload.fromDate,
      reportName: reportPayload.reportName,
    });
    initiateReportGeneration(payload);
  };

  let generateButtonDisabled = true;

  const anyReportSelected = Object.values(parameters.selectedReports).some((report) => report.selected);
  if (anyReportSelected) {
    generateButtonDisabled = false;
  }

  return (
    <>
      <ItemOrdering
        itemsToSort={getReportsToSort(parameters.reportsToSort, parameters.reportFormat)}
        setSelectedReport={setSelectedReportOrderParameter}
        setShowItemOrderingModal={setShowItemOderingModalParameter}
        generateReport={() => setShowItemOderingModalParameter(false)}
        isOpen={parameters.showItemOrdering}
      />
      <Box display="flex" justifyContent={'space-between'}>
        <Box paddingRight={'10px'}>
          <Typography variant="h2" style={{ paddingLeft: '10px' }}>
            Reports
          </Typography>
        </Box>
        <Box display="flex" justifyContent={'space-between'}>
          {reportGeneration.isGenerating && <Alert severity="success">Report in progress</Alert>}
        </Box>
        <Box display="flex" alignItems="center" gap={2}>
          {!hasClientDirectAccess && (
            <Box display="flex" gap={2} alignItems="center">
              <Typography>Load From </Typography>
              <ReportingParameterSelect
                disabled={getReportParametersByIdProgress.isLoading}
                items={savedParametersList}
                selectedParameter={parameters?.selectedParameter ?? null}
                setParameter={setSelectedReportingParameter}
              />
            </Box>
          )}
          <ProgressButton
            progress={generateProgress}
            startIcon={Cached}
            disabled={generateButtonDisabled}
            style={{ borderRadius: 24, height: 48, width: 132 }}
            color={'primary'}
            onClick={() => {
              setReportingParameter({
                ...reportingParameter,
              });
              generateReportParameters();
            }}
            variant="contained"
          >
            Generate
          </ProgressButton>
        </Box>
      </Box>
    </>
  );
};
